/**
 * Iter 189 — Operations · Timing dashboard.
 *
 * Lists every completed collection with field-staff name, customer,
 * items collected, bags, weight, start/submit time, and duration.
 * Filters: date range, LSGD, health zone, field staff. Exports CSV / PDF.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Clock, FilePdf, FileCsv } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import AppShell from "../../components/layout/AppShell";

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }); }
  catch { return iso; }
};
const fmtDur = (min) => {
  if (min == null) return "—";
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export default function OperationsTiming() {
  const [filters, setFilters] = useState({
    date_from: daysAgoIso(6),
    date_to: todayIso(),
    lsgd_id: "",
    health_zone_id: "",
    field_staff_id: "",
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refs, setRefs] = useState({ lsgds: [], zones: [], staff: [] });

  useEffect(() => {
    // Reference lists (silent — filters still work without them).
    Promise.all([
      // Iter 189.1 — reference lists come from the location + health-
      // zone routes; there is no plain /api/lsgds endpoint.
      api.get("/locations?type=lsgd").catch(() => ({ data: [] })),
      api.get("/special-health-zones").catch(() => ({ data: [] })),
      api.get("/staff").catch(() => ({ data: [] })),
    ]).then(([l, z, s]) => {
      const arr = (d) => Array.isArray(d) ? d : (d.items || d.locations || d.zones || []);
      setRefs({ lsgds: arr(l.data), zones: arr(z.data), staff: arr(s.data) });
    });
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await api.get(`/operations/timing?${params}`);
      setRows(r.data.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const setF = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));

  const totals = useMemo(() => {
    return rows.reduce((acc, r) => ({
      bags: acc.bags + (r.bag_count || 0),
      kg: acc.kg + (r.total_weight_kg || 0),
      minutes: acc.minutes + (r.duration_min || 0),
    }), { bags: 0, kg: 0, minutes: 0 });
  }, [rows]);

  const download = async (fmt) => {
    try {
      const token = localStorage.getItem("rescigo_token");
      const params = new URLSearchParams({ fmt });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/operations/timing/export?${params}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`Export failed (${r.status})`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fmt === "pdf" ? "ops-timing.pdf" : "ops-timing.csv";
      a.click(); URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch (e) { toast.error(e?.message || formatApiError(e)); }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4" data-testid="operations-timing">
        <header className="flex items-center gap-2">
          <Clock size={22} weight="duotone" className="text-[#2D5A27]"/>
          <div>
            <h1 className="font-heading text-2xl">Operations timing</h1>
            <p className="text-[12px] text-[#5C5A55]">
              How long each field staff spent per customer — filter, sort, export.
            </p>
          </div>
        </header>

        {/* Filters */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-3 grid grid-cols-2 sm:grid-cols-5 gap-2"
             data-testid="ops-timing-filters">
          <FilterField label="From">
            <input type="date" value={filters.date_from}
                   onChange={e => setF("date_from", e.target.value)}
                   data-testid="ops-timing-f-date-from"
                   className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-[13px]"/>
          </FilterField>
          <FilterField label="To">
            <input type="date" value={filters.date_to}
                   onChange={e => setF("date_to", e.target.value)}
                   data-testid="ops-timing-f-date-to"
                   className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-[13px]"/>
          </FilterField>
          <FilterField label="LSGD">
            <select value={filters.lsgd_id}
                    onChange={e => setF("lsgd_id", e.target.value)}
                    data-testid="ops-timing-f-lsgd"
                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-[13px]">
              <option value="">All</option>
              {refs.lsgds.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </FilterField>
          <FilterField label="Health zone">
            <select value={filters.health_zone_id}
                    onChange={e => setF("health_zone_id", e.target.value)}
                    data-testid="ops-timing-f-zone"
                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-[13px]">
              <option value="">All</option>
              {refs.zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
          </FilterField>
          <FilterField label="Field staff">
            <select value={filters.field_staff_id}
                    onChange={e => setF("field_staff_id", e.target.value)}
                    data-testid="ops-timing-f-staff"
                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-[13px]">
              <option value="">All</option>
              {refs.staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </FilterField>
        </div>

        {/* Toolbar + totals */}
        <div className="flex flex-wrap items-center gap-3 bg-[#F7F6F2] border border-[#E5E2D9] rounded-md px-3 py-2 text-[12px]">
          <span data-testid="ops-timing-count" className="font-semibold text-[#1A1A1A]">
            {rows.length} collection{rows.length !== 1 ? "s" : ""}
          </span>
          <span className="text-[#8A8780]">·</span>
          <span data-testid="ops-timing-total-bags">
            <strong>{totals.bags}</strong> bags
          </span>
          <span className="text-[#8A8780]">·</span>
          <span data-testid="ops-timing-total-kg">
            <strong>{totals.kg.toFixed(2)}</strong> kg
          </span>
          <span className="text-[#8A8780]">·</span>
          <span data-testid="ops-timing-total-dur">
            <strong>{fmtDur(totals.minutes)}</strong> spent on-site
          </span>
          <div className="ml-auto flex gap-2">
            <button onClick={() => download("csv")}
                     data-testid="ops-timing-export-csv"
                     className="h-8 px-3 rounded border border-[#E5E2D9] bg-white hover:bg-[#F0ECE3] inline-flex items-center gap-1 text-[12px]">
              <FileCsv size={12}/> CSV
            </button>
            <button onClick={() => download("pdf")}
                     data-testid="ops-timing-export-pdf"
                     className="h-8 px-3 rounded border border-[#E5E2D9] bg-white hover:bg-[#F0ECE3] inline-flex items-center gap-1 text-[12px]">
              <FilePdf size={12}/> PDF
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="ops-timing-table">
            <thead>
              <tr className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.14em] text-[#5C5A55]">
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Field staff</th>
                <th className="text-left p-2">Customer</th>
                <th className="text-left p-2">Items collected</th>
                <th className="text-right p-2">Bags</th>
                <th className="text-right p-2">Weight (kg)</th>
                <th className="text-left p-2">Start</th>
                <th className="text-left p-2">Submit</th>
                <th className="text-right p-2">Duration</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="p-6 text-center italic text-[#8A8780]">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center italic text-[#8A8780]">
                  No completed collections in this window.
                </td></tr>
              )}
              {rows.map(r => (
                <tr key={r.task_id} className="border-t border-[#F0ECE3] hover:bg-[#F7F6F2]"
                    data-testid={`ops-timing-row-${r.task_id}`}>
                  <td className="p-2 font-mono text-[11px]">{r.date}</td>
                  <td className="p-2">{r.field_staff_name}</td>
                  <td className="p-2">
                    <div className="font-medium">{r.customer_name}</div>
                    {r.customer_code && <div className="text-[10px] font-mono text-[#8A8780]">{r.customer_code}</div>}
                  </td>
                  <td className="p-2 text-[11px]">{r.items_collected}</td>
                  <td className="p-2 text-right font-mono">{r.bag_count}</td>
                  <td className="p-2 text-right font-mono">{r.total_weight_kg?.toFixed(2)}</td>
                  <td className="p-2 font-mono text-[11px]">{fmtTime(r.start_time)}</td>
                  <td className="p-2 font-mono text-[11px]">{fmtTime(r.submit_time)}</td>
                  <td className="p-2 text-right font-mono">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      r.duration_min == null ? "text-[#8A8780]"
                        : r.duration_min < 15 ? "bg-[#DCEEDC] text-[#1A3317]"
                        : r.duration_min < 45 ? "bg-[#F9E4B7] text-[#7A5A11]"
                        : "bg-[#F9EDE7] text-[#7A2E18]"
                    }`}>
                      {fmtDur(r.duration_min)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

const FilterField = ({ label, children }) => (
  <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
    <div className="mb-1">{label}</div>
    {children}
  </label>
);
